import { nanoId } from '@kanbu/shared';
import { t } from '@lingui/core/macro';
import { Button, cn, Label } from '@utima/ui';
import { useField } from 'informed';
import { memo, useCallback, useEffect, useState } from 'react';

import { ListInputRow } from '@/components/listInput/ListInputRow';

export interface ListInputProps {
  /**
   * Name of the input (used in informed)
   */
  name: string;
  /**
   * Label of the input
   */
  label?: string;
  /**
   * Type of the input lines (only text is supported for now)
   */
  type: 'text';
  /**
   * Amount of lines to start with.
   *
   * @default 0
   */
  startItemsAmount?: number;
  /**
   * Callback for value changes in the input.
   * It will be invoked every time any value in the list changes.
   */
  onChange?: (value: string[]) => void;
  /**
   * Minimum amount of lines to show, user cannot remove them.
   *
   * @default 0
   */
  minLines?: number;
  /**
   * If true, empty lines will be submitted as well.
   * In the result array, the empty lines will be present as ''.
   *
   * @default false
   */
  submitEmptyLines?: boolean;
  /**
   * If true, the values will be trimmed before submitting.
   * This does not remove empty lines!
   *
   * @default false
   */
  trimValues?: boolean;
  /**
   * Class name for the container div (this whole box component that's wrapping lines)
   */
  containerClassName?: string;
  /**
   * Label for the "add new item" button
   *
   * @default 'Add new item'
   */
  addItemLabel?: string;
  /**
   * Title for the "add new item" button
   */
  addItemTitle?: string;
  /**
   * Title for the "remove item" button
   *
   * @default 'Remove'
   */
  removeItemTitle?: string;
  /**
   * Default value for the input.
   */
  defaultValue?: string[];
}

/**
 * TODO:
 *  - Support for more types (at least number)
 *  - Support passing in "value" prop instead of using informed only
 *  - Implement max amount of lines
 *  - Optimize for rendering performance
 *  - Optimize for memory usage, now we are creating new array on every change and every line has its own internal value state
 */
export const ListInput = memo(function ListInput({
  name,
  type,
  label,
  onChange,
  containerClassName,
  minLines = 0,
  startItemsAmount = 0,
  trimValues = false,
  submitEmptyLines = false,
  addItemLabel,
  addItemTitle,
  defaultValue = [],
  removeItemTitle,
}: ListInputProps) {
  const { fieldApi, fieldState } = useField<unknown, string[]>({
    name,
    defaultValue,
  });

  const { setValue, setTouched, setFocused } = fieldApi;

  const [items, setItems] = useState<{ id: string; value: string }[]>(() => {
    if (!fieldState.value || !Array.isArray(fieldState.value)) return [];

    // Set initial values from informed fieldState
    return (fieldState.value as string[]).map(val => ({
      id: nanoId(),
      value: val,
    }));
  });

  // Convert items to string array for the form
  const updateFormValue = useCallback(
    (newItems: { id: string; value: string }[]) => {
      let stringValues = newItems.map(item => {
        if (trimValues) return item.value.trim();

        return item.value;
      });

      if (!submitEmptyLines) {
        // Remove empty lines from the array
        stringValues = stringValues.filter(val => val !== '');
      }

      setValue(stringValues);
      setTouched(true);
      onChange?.(stringValues);
    },
    [submitEmptyLines, setValue, setTouched, onChange, trimValues],
  );

  // Handle item value change
  const handleItemChange = (index: number, newValue: string) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], value: newValue };
    setItems(() => updatedItems);
    updateFormValue(updatedItems);
  };

  // Handle item removal
  const handleItemRemove = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(() => updatedItems);
    updateFormValue(updatedItems);
  };

  // Add new item
  const handleAddItem = () => {
    const updatedItems = [...items, { id: nanoId(), value: '' }];
    setItems(() => updatedItems);
    updateFormValue(updatedItems);
  };

  // Set initial items based on the minLines prop
  useEffect(() => {
    if (minLines <= items.length) return;

    const newItems = Array.from({ length: minLines }, (_, i) => {
      if (items[i]) {
        return items[i];
      }

      return { id: nanoId(), value: '' };
    });

    setItems(() => newItems);
    updateFormValue(newItems);
  }, [items, minLines, updateFormValue]);

  // Set initial items based on the startItemsAmount prop
  useEffect(() => {
    if (startItemsAmount <= items.length) return;

    const newItems = Array.from({ length: startItemsAmount }, (_, i) => {
      if (items[i]) {
        return items[i];
      }

      return { id: nanoId(), value: '' };
    });
    setItems(() => newItems);
    updateFormValue(newItems);
    // This effect should be run only at the start
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={cn('flex', 'flex-col', 'gap-2', containerClassName)}
      onFocus={() => setFocused(true)}
    >
      <Label>{label}</Label>
      {items.map((inputValue, index) => (
        <ListInputRow
          disableRemove={items.length <= minLines}
          name={`${name}_${inputValue.id}`}
          key={inputValue.id}
          type={type}
          initialValue={inputValue.value}
          onChange={value => handleItemChange(index, value)}
          onRemove={() => handleItemRemove(index)}
          removeButtonTitle={removeItemTitle || t`Remove`}
        />
      ))}
      <Button
        data-cta='admin-list-input-add-item-button'
        title={addItemTitle}
        type='button'
        onClick={handleAddItem}
      >
        {addItemLabel || t`Add new item`}
      </Button>
    </div>
  );
});
